// brokersFilterComponent.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Card, CardContent, Box, Tooltip, IconButton, InputAdornment, Icon as MuiIcon, Autocomplete, TextField } from "@mui/material";
import { Add as AddIcon, Search as SearchIcon, Event as CalendarIcon, Clear as ClearIcon, Download, Description } from "@mui/icons-material";
import MDButton from "../../components/MDButton";
import MDDatePicker from "../../components/MDDatePicker";
import MDInput from "../../components/MDInput";
import OffersSelect from "../offers/OfferCustomSelect";

interface OptionType {
  label: string;
  value: string | number;
}

interface BrokersHeaderProps {
  onAddBroker: () => void;
  onViewDetails: () => void;
  onResetFilters: () => void;
  onSelectBroker: (brokerIds: number[], brokerNames: string[]) => void;
  onDateChange: (filterType: string, value: any) => void;
  onSearchChange: (value: string) => void;
  onExportData: () => void;
  onExportLedgerReport: () => void;
  selectedBrokerRow: any;
  selectedBroker: any;
  selectedBrokerIds: number[];
  selectedBrokerNames: string[];
  rangeOpen: boolean;
  setRangeOpen: React.Dispatch<React.SetStateAction<boolean>>;
  filters: {
    fromDate: any;
    toDate: any;
    byPeriod: any;
    searchTerm: string;
    salesRep?: string | null;
  };
  data: any[];
  ledgerLoading: boolean;
}

const PERIOD_OPTIONS: OptionType[] = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Quarter", value: "This Quarter" },
  { label: "This Year", value: "This Year" },
  { label: "All Time", value: "All Time" },
];

const NAVY = "#233B69";
const NAVY_DARK = "#1A2C4F";
const LIGHT_FILL = "#F4F7FB";

const pillSx = {
  height: 36,
  backgroundColor: "#fff",
  color: "#3D4B62",
  border: "1px solid #CED4DA",
  borderRadius: "10px",
  fontSize: 13,
  fontWeight: 500,
  textTransform: "none" as const,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
  whiteSpace: "nowrap" as const,
  lineHeight: 1,
  px: 1,
  "&:hover": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
};

const dateInputSx = {
  "& .MuiInputBase-root": {
    height: 36,
    fontSize: 13,
    backgroundColor: "#fff",
    color: "#3D4B62",
    borderRadius: "5px",
    fontWeight: 500,
    "&:hover": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
    "&.Mui-focused": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
    "& .MuiInputBase-input": { textAlign: "left", padding: "0 8px !important" },
  },
};

const asOption = (v?: string | null): OptionType | null => (v ? { label: v, value: v } : null);
const optToStringOrNull = (opt: any): string | null => {
  if (!opt || Array.isArray(opt)) return null;
  const v = opt.value;
  if (v == null) return null;
  const s = typeof v === "string" ? v.trim() : String(v);
  return s.length ? s : null;
};

const PeriodSelect: React.FC<{
  options: OptionType[];
  value: string | null;
  onChange: (next: string | null) => void;
}> = ({ options, value, onChange }) => {
  const selected = value ? { label: value, value } : null;
  return (
    <Autocomplete
      options={options}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
      value={selected}
      onChange={(_, newVal: OptionType | null) => onChange(newVal ? String(newVal.value) : null)}
      isOptionEqualToValue={(o, v) => o.value === v?.value}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Filter By Period"
          fullWidth
          sx={{ minWidth: 0 }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ pl: 0.25 }}>
                <CalendarIcon sx={{ fontSize: 15, color: "#6B7280" }} />
              </InputAdornment>
            ),
            endAdornment: params.InputProps.endAdornment,
            sx: {
              height: 36,
              fontSize: 13,
              backgroundColor: "#fff",
              color: "#3D4B62",
              borderRadius: "5px",
              fontWeight: 500,
              "&:hover": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
              "&.Mui-focused": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
              "& .MuiInputBase-input": {
                padding: "0 6px !important",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: "36px",
              },
              "& .MuiAutocomplete-endAdornment": { right: 6 },
            },
          }}
        />
      )}
      disableClearable={false}
    />
  );
};

const BrokersHeader: React.FC<BrokersHeaderProps> = ({
  onAddBroker,
  onViewDetails,
  onResetFilters,
  onSelectBroker,
  onDateChange,
  onSearchChange,
  onExportData,
  onExportLedgerReport,
  selectedBrokerRow,
  selectedBroker,
  selectedBrokerIds,
  selectedBrokerNames,
  rangeOpen,
  setRangeOpen,
  filters,
  data,
  ledgerLoading,
}) => {
  const brokerOptions: OptionType[] = useMemo(() => {
    const seen = new Set<string>();
    const options = (data || [])
      .filter((r: any) => r.isBroker)
      .map((r: any) => ({
        label: r.name ?? r.brokerName ?? "",
        value: String(r.partyId ?? r.id ?? ""),
      }))
      .filter((o: OptionType) => o.label && o.value);
    const unique = options.filter((o) => {
      const key = `${o.value}|${o.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const brokerIdToName = useMemo(() => {
    const m = new Map<string, string>();
    (data || []).forEach((r: any) => {
      if (r.isBroker) {
        const id = String(r.partyId ?? r.id ?? "");
        const nm = r.name ?? r.brokerName ?? "";
        if (id && nm) m.set(id, nm);
      }
    });
    return m;
  }, [data]);

  const selectedBrokerOption: OptionType | null =
    selectedBrokerIds.length
      ? {
          label: selectedBrokerNames[0] || brokerIdToName.get(String(selectedBrokerIds[0])) || "",
          value: selectedBrokerIds[0].toString(),
        }
      : null;

  const salesRepOptions: OptionType[] = useMemo(() => {
    const allowed = selectedBrokerIds.length ? new Set(selectedBrokerIds.map(String)) : null;
    const reps = (data || [])
      .filter((r: any) => !r.isBroker)
      .filter((r: any) => (allowed ? allowed.has(String(r.brokerId)) : true))
      .map((r: any) => r.salesRep ?? r.contactPerson ?? null)
      .filter((v: any) => !!v && v !== "null")
      .map((v: string) => v.trim())
      .filter(Boolean);
    return Array.from(new Set(reps)).map((v) => ({ label: v, value: v }));
  }, [data, selectedBrokerIds]);

  const handleBrokerChange = (opt: any) => {
    const val = opt?.value ? String(opt.value) : null;
    if (val) {
      const name = opt?.label ?? brokerIdToName.get(val) ?? "";
      onSelectBroker([parseInt(val, 10)], [name]);
      onDateChange("salesRep", null);
    } else {
      onSelectBroker([], []);
      onDateChange("salesRep", null);
    }
  };

  const handlePeriodChange = (next: string | null) => onDateChange("byPeriod", next);
  const handleSalesRepChange = (opt: any) => onDateChange("salesRep", opt ? String(opt.value) : null);

  const [searchDraft, setSearchDraft] = useState<string>(filters.searchTerm || "");

  useEffect(() => {
    if ((filters.searchTerm || "") !== searchDraft) setSearchDraft(filters.searchTerm || "");
  }, [filters.searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== (filters.searchTerm || "")) onSearchChange(searchDraft);
    }, 250);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const clearFromDate = useCallback(() => onDateChange("fromDate", null), [onDateChange]);
  const clearToDate = useCallback(() => onDateChange("toDate", null), [onDateChange]);

  return (
    <Card elevation={0} sx={{ width: "100%", mb: 1, ml: 1, border: "1px solid #E5E7EB", borderRadius: "10px", backgroundColor: "#FBFCFE" }}>
      <CardContent sx={{ py: 1 }}>
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap" }}>
          <Box sx={{ display: "flex", gap: 0.5, flex: "0 0 auto" }}>
            <MDButton
              variant="gradient"
              color="primary"
              onClick={onAddBroker}
              sx={{ ...pillSx, px: 1, minWidth: 110, color: "#fff", "&:hover": { backgroundColor: "#1f2937" } }}
            >
              <AddIcon fontSize="small" />
              Add ISO/Broker
            </MDButton>

            <Tooltip title={!selectedBroker ? "Please select the broker" : ""} arrow placement="top">
              <span>
                <MDButton
                  color="dark"
                  onClick={onViewDetails}
                  disabled={!selectedBroker}
                  sx={{ ...pillSx, px: 1, minWidth: 64, backgroundColor: "#111827", color: "#fff", "&:hover": { backgroundColor: "#1f2937" } }}
                >
                  View
                </MDButton>
              </span>
            </Tooltip>

            <Tooltip title="Reset All Filters" arrow>
              <IconButton
                size="medium"
                onClick={onResetFilters}
                sx={{ width: 25, height: 40, borderRadius: "6px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <MuiIcon sx={{ fontSize: 32, color: "#6B7280" }}>filter_alt_off</MuiIcon>
              </IconButton>
            </Tooltip>
          </Box>

      

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
            <Box sx={{ flex: "1 1 0", minWidth: 120 }}>
              <OffersSelect
                options={brokerOptions}
                label="Broker/ISO Shop"
                value={selectedBrokerOption}
                onChange={handleBrokerChange}
                width="100%"
                height={36}
                backgroundColor="#ffffff"
                borderColor="#CED4DA"
                borderRadius="5px"
                fontSize="13px"
                fontWeight={500}
                textColor="#3D4B62"
                hoverBgColor="#F6F8FA"
                hoverBorderColor="#B9C0C7"
                disableClearable={false}
                filterOptions={true}
              />
            </Box>

            <Box sx={{ flex: "0 0 clamp(200px, 25%, 200px)", minWidth: 100 }}>
              <OffersSelect
                options={salesRepOptions}
                label="Sales Rep"
                value={asOption(filters.salesRep ?? null)}
                onChange={handleSalesRepChange}
                width="100%"
                height={36}
                backgroundColor="#ffffff"
                borderColor="#CED4DA"
                borderRadius="5px"
                fontSize="13px"
                fontWeight={500}
                textColor="#3D4B62"
                hoverBgColor="#F6F8FA"
                hoverBorderColor="#B9C0C7"
                disableClearable={false}
                filterOptions={true}
              />
            </Box>

            <Box sx={{ flex: "0 0 clamp(120px, 15%, 160px)", minWidth: 110 }}>
              <MDDatePicker
                input={{
                  placeholder: "Filter From Date",
                  sx: { ...dateInputSx, width: "100%" },
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ pl: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: filters.fromDate ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearFromDate} aria-label="Clear from" sx={{ mr: 0.25 }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                InputLabelProps={{ sx: { display: "none" } }}
                value={filters.fromDate}
                onChange={(date: any) => onDateChange("fromDate", date)}
              />
            </Box>

            <Box sx={{ flex: "0 0 clamp(120px, 13%, 160px)", minWidth: 110 }}>
              <MDDatePicker
                input={{
                  placeholder: "Filter To Date",
                  sx: { ...dateInputSx, width: "100%" },
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start" sx={{ pl: 0.5 }}>
                        <CalendarIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                      </InputAdornment>
                    ),
                    endAdornment: filters.toDate ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearToDate} aria-label="Clear to" sx={{ mr: 0.25 }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
                InputLabelProps={{ sx: { display: "none" } }}
                value={filters.toDate}
                onChange={(date: any) => onDateChange("toDate", date)}
              />
            </Box>

            <Box sx={{ flex: "1 1 150px", minWidth: 160, maxWidth: "17%" }}>
              <PeriodSelect options={PERIOD_OPTIONS} value={filters.byPeriod ?? null} onChange={handlePeriodChange} />
            </Box>

            <Box sx={{ flex: "0 0 150px", minWidth: 150 }}>
              <MDInput
                placeholder="Search"
                variant="outlined"
                size="small"
                value={searchDraft}
                onChange={(e: any) => setSearchDraft(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                      <SearchIcon sx={{ color: "#6B7280", fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "#fff",
                  borderRadius: "5px",
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    fontSize: 13,
                    borderRadius: "5px",
                    color: "#3D4B62",
                    "&:hover": { borderColor: "#B9C0C7", backgroundColor: "#F6F8FA" },
                    "& .MuiInputBase-input": { px: 1 },
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.4, flex: "0 0 auto" }}>
            <MDButton
              variant="outlined"
              onClick={onExportData}
              startIcon={<Download />}
              sx={{
                ...pillSx,
                minWidth: 120,
                height: "32px !important",
                px: 2,
                fontWeight: 500,
                letterSpacing: 0.2,
                backgroundColor: LIGHT_FILL,
                borderColor: NAVY,
                color: "#233B69 !important",
                "&:hover": { backgroundColor: "#ECF2FA", borderColor: NAVY_DARK, color: NAVY_DARK },
                "&.Mui-disabled": { backgroundColor: "#f5f5f5", color: "#9AA6BF", borderColor: "#D6DCE8" },
              }}
            >
              EXPORT CSV
            </MDButton>

            <MDButton
              variant="outlined"
              onClick={onExportLedgerReport}
              disabled={ledgerLoading}
              startIcon={<Description />}
              sx={{
                ...pillSx,
                minWidth: 100,
                height: "32px !important",
                px: 2,
                fontWeight: 500,
                letterSpacing: 0.3,
                backgroundColor: LIGHT_FILL,
                borderColor: NAVY,
                color: NAVY,
                "&:hover": { backgroundColor: "#ECF2FA", borderColor: NAVY_DARK, color: NAVY_DARK },
                "&.Mui-disabled": { backgroundColor: "#f5f5f5", color: "#9AA6BF", borderColor: "#D6DCE8" },
              }}
            >
              LEDGER
            </MDButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BrokersHeader;
